import { useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { QuotaMeStatusDTO } from '@shared/schemas/quota';
import { getCurrentQuotaStatus } from './quota-api';
import { getEffectiveQuotaConstraint } from './quota-constraint';
import { currentQuotaQueryKey } from './quota-query-keys';

export const BLOCKED_QUOTA_REFETCH_INTERVAL_MS = 60_000;

export function getBlockedQuotaRefetchInterval(status: QuotaMeStatusDTO | undefined) {
  return status && getEffectiveQuotaConstraint(status).blocked
    ? BLOCKED_QUOTA_REFETCH_INTERVAL_MS
    : false;
}

export function shouldRefetchQuotaAfterStream(status: QuotaMeStatusDTO | undefined) {
  return !status || status.enforced || status.company.monthlyTokenLimit !== null;
}

export function useQuotaStatus() {
  const query = useQuery({
    queryFn: getCurrentQuotaStatus,
    queryKey: currentQuotaQueryKey,
    refetchInterval: (currentQuery) => getBlockedQuotaRefetchInterval(currentQuery.state.data),
    refetchOnWindowFocus: (currentQuery) => currentQuery.state.data?.enforced !== false,
  });
  const { data, refetch } = query;

  const refetchImmediately = useCallback(async () => {
    await refetch();
  }, [refetch]);

  const refetchAfterStream = useCallback(async () => {
    if (shouldRefetchQuotaAfterStream(data)) {
      await refetch();
    }
  }, [data, refetch]);

  return {
    ...query,
    blocked: data ? getEffectiveQuotaConstraint(data).blocked : false,
    refetchAfterStream,
    refetchImmediately,
    status: data,
  };
}
